import { useState, useEffect } from 'react';
import { fetchCampaigns } from 'api/emailHistory/campaignsApi';

/**
 * @hook useCampaigns
 * @description Fetches campaigns data with pagination, sorting, and filtering
 * @param {Object} filters - Filter parameters from URL state
 * @returns {Object} Object containing campaigns data, stats, loading state, and error
 */
export function useCampaigns(filters) {
  const [campaigns, setCampaigns] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [stats, setStats] = useState({
    totalCampaigns: 0,
    totalBulkSends: 0,
    avgDeliveryRate: 0,
    avgOpenRate: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadCampaigns = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await fetchCampaigns({
          page: filters.page,
          pageSize: filters.pageSize,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
          search: filters.search || null,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
        });

        setCampaigns(data.rows || []);
        setTotalRows(data.totalRows || 0);

        // Use stats from API response (calculated server-side across all matching records)
        const apiStats = data.stats || {};
        setStats({
          totalCampaigns: apiStats.totalCampaigns ?? data.totalRows ?? 0,
          totalBulkSends: apiStats.totalBulkSends ?? 0,
          avgDeliveryRate: typeof apiStats.avgDeliveryRate === 'number' 
            ? apiStats.avgDeliveryRate.toFixed(2) 
            : '0.00',
          avgOpenRate: typeof apiStats.avgOpenRate === 'number' 
            ? apiStats.avgOpenRate.toFixed(2) 
            : '0.00',
        });
      } catch (err) {
        console.error('Error fetching campaigns:', err);
        setError(err.message || 'Failed to load campaigns');
        setCampaigns([]);
        setTotalRows(0);
      } finally {
        setIsLoading(false);
      }
    };

    loadCampaigns();
  }, [filters.page, filters.pageSize, filters.sortBy, filters.sortOrder, filters.search, filters.dateFrom, filters.dateTo]);

  return {
    campaigns,
    totalRows,
    stats,
    isLoading,
    error,
  };
}

